/**
 * Derive editable **stored cabinet runs** from a generated `room.cabinetLayout`
 * — the inverse of `fromPhaseB.js`. This is what makes an auto kitchen editable:
 * one `room.cabinetRuns[]` entry per cabinet wall, so the existing manual
 * per-bay / appearance edit UI + override solver operate on auto output.
 *
 * Scope: one run per `real` or `peninsula` cabinet wall, then JOINED at every
 * corner the layout put an L in (`joinAutoRunsAtCorners`) — a room vertex
 * between two real walls, or a peninsula's ROOT on the wall it springs from —
 * so a U whose back is a peninsula is one polyline run and `fillRun.cornerPlan`
 * owns its vertices on the first edit (single-run plan, 2026-09-12). Corners
 * the layout left open or blind, and every corner body a run does not own,
 * are held open as hard `corner` bays (`cornerReservationsForRuns`). Islands
 * stay on `buildIsland*`.
 *
 * Stored-run shape (Firestore-serializable, per cabinet-runs/overview.md):
 *   { id, source:'auto', segments:[{ wallId, kind, startParam, endParam }],
 *     appliances:[{ id, type, segmentIndex, centerParam, footprint? }] }
 * `bayOverrides` / `appearance` / `uppers` are added lazily by the edit UI.
 *
 * `kind` is carried on the segment (unlike a manual run, whose segment is a bare
 * structure `wallId`) so the edit-time resolver can dispatch real vs imaginary
 * through `resolveCabinetWall`. `source:'auto'` marks the run for that dispatch.
 *
 * Pure. No I/O.
 */

const { getWallAxes } = require('../wallMath');
const { footprintFor } = require('../../geometry/objectFootprint');
const { applianceBayFromLegacy } = require('../../cabinet-runs/bays');
const { joinRunsAtCorners } = require('../../cabinet-runs/cornerJoin');
const { stampApplianceBays } = require('../../cabinet-runs/ownership');
const {
  applianceObjectIds,
  isInstallPieceOf,
} = require('../../cabinet-runs/applianceInstall');
const { stripFillerCabinetIdOf } = require('../buildCornerFillCountertop');
const { isRunEndPocket } = require('../runEndPocket');
const {
  isPeninsulaBackBridge,
  isPeninsulaBackPanel,
} = require('../../cabinet-runs/peninsulaBack');
const {
  isShallowRunPiece,
  runForPieceOnWall,
  shallowStorageRuns,
} = require('./shallowRuns');
const {
  stampRunFrames,
  rebaseRunFrames,
} = require('../../cabinet-runs/wallFrame');
const { dedupeRoomWalls } = require('../wallDedupe');
const { buildCabinetWallResolver } = require('./resolveCabinetWall');
const { toRunApplianceType } = require('./wallToRun');

// Cabinet-wall kinds that become editable single-wall runs. Island is excluded
// (stays on the island builder); anything else is ignored defensively.
const RUN_KINDS = new Set(['real', 'peninsula']);

/**
 * @param {Object} room  room doc with `walls` + `cabinetLayout`.
 * @param {Object} [options]
 * @param {Object} [options.cabinetLayout]  defaults to `room.cabinetLayout`.
 * @returns {{ runs: Object[], warnings: string[] }}
 *   `runs` are stored-shape cabinet runs (real + peninsula, single-wall).
 *   `warnings` note cabinet walls that couldn't be resolved (e.g. a real wall
 *   the kitchen polygon dropped) — surfaced, never silently swallowed.
 */
function cabinetLayoutToRuns(room, options = {}) {
  const cabinetLayout = options.cabinetLayout || room?.cabinetLayout || null;
  const runs = [];
  const warnings = [];
  if (!cabinetLayout?.cabinetWalls?.length) return { runs, warnings };

  const { resolve } = buildCabinetWallResolver(room, cabinetLayout);
  const rawWallById = new Map(
    dedupeRoomWalls(room?.walls || []).map((w) => [w.id, w])
  );

  // Index persisted appliance anchors by the cabinet wall that hosts them.
  const appliancesByWall = new Map();
  for (const appliance of cabinetLayout.appliances || []) {
    const wallId = appliance?.cabinetWallRef?.id;
    if (!wallId) continue;
    if (!appliancesByWall.has(wallId)) appliancesByWall.set(wallId, []);
    appliancesByWall.get(wallId).push(appliance);
  }

  // Walls the layout put no run on (`carriesRun: false`): a run is emitted
  // only around the appliances parked there, and it never joins a neighbour.
  const runlessWallIds = new Set(
    cabinetLayout.cabinetWalls
      .filter((cw) => cw.carriesRun === false)
      .map((cw) => cw.id)
  );

  for (const cw of cabinetLayout.cabinetWalls) {
    if (!RUN_KINDS.has(cw.kind)) continue;
    if (cw.carriesRun === false && !(appliancesByWall.get(cw.id) || []).length)
      continue;

    const wall = resolve({ id: cw.id, kind: cw.kind });
    if (!wall) {
      warnings.push(
        `cabinet wall ${cw.id} (${cw.kind}) did not resolve — no run emitted`
      );
      continue;
    }
    const axes = getWallAxes(wall);
    if (!axes) {
      warnings.push(`cabinet wall ${cw.id} has degenerate geometry`);
      continue;
    }

    // The extent the generator FILLED, not the whole wall — see
    // `emittedExtentOnWall`. Falls back to the full wall for a caller that
    // cannot see the layout's objects.
    // A wall that persists the plan's built spans (`cw.built`) answers from
    // them; a layout from before that reads its objects.
    const planned = Array.isArray(cw.built) ? cw.built : null;
    const emitted = planned
      ? plannedExtent(planned, axes)
      : emittedExtentOnWall(
          cw.id,
          options.objects,
          axes,
          runBoundsOnWall(
            axes,
            cw.kind === 'real' ? rawWallById.get(cw.id) : null
          ),
          new Set((appliancesByWall.get(cw.id) || []).map((a) => a.id))
        );
    // A caller that showed us the layout's objects — or a plan — and built
    // NOTHING on this wall (a shallow storage run only, say) has no run here;
    // the full-wall default is for a caller that cannot see either.
    if (!emitted && (planned || options.objects?.length)) {
      if (!(appliancesByWall.get(cw.id) || []).length) continue;
    }
    const extent = emitted || {
      startParam: 0,
      endParam: axes.wallLen,
    };
    const appliances = [];
    for (const appliance of appliancesByWall.get(cw.id) || []) {
      const type = toRunApplianceType(appliance.type);
      // wallOven (and any type the run engine can't own) is left to
      // materializeCandidate — skip it here rather than crash the solver.
      if (!type) continue;
      const anchor = {
        id: appliance.id,
        type,
        segmentIndex: 0,
        centerParam: appliance.wallParam,
      };
      // The installation's group: the engine's sink base JOINS it instead of
      // minting a `manualSink_` group of its own, which split the generator's
      // sink install in two on the first re-solve (the basin and faucet in
      // one group, the base the engine built in another).
      if (appliance.fixtureGroupId)
        anchor.fixtureGroupId = appliance.fixtureGroupId;
      // Carry the real footprint if generation persisted it, so an edit-time
      // re-solve reserves the true appliance width (not the standard-tier
      // fallback in appliances.footprintFor). Omitted if absent.
      if (appliance.width != null || appliance.depth != null) {
        anchor.footprint = { width: appliance.width, depth: appliance.depth };
      }
      // An oven column's sections, so the run claims all three
      // (`emitOvenColumns`) rather than packing around the base alone.
      if (appliance.column) anchor.column = appliance.column;
      // A fridge the layout stood free stays free on every re-solve
      // (`fridgeIsBuiltIn`); only option #1 marks one.
      if (type === 'fridge' && appliance.builtIn === false)
        anchor.builtIn = false;
      appliances.push(anchor);
    }

    runs.push({
      id: `autorun_${cw.id}`,
      source: 'auto',
      // A peninsula has no wall above it to hang anything from. `fillRun` fills
      // uppers unless a run says otherwise, so without this the first edit of a
      // generated kitchen hung a row of wall cabinets in mid-air over the
      // peninsula — geometry the generator itself never produces.
      ...(cw.kind === 'peninsula' ? { uppers: false } : {}),
      segments: [{ wallId: cw.id, kind: cw.kind, ...extent }],
      // Emitted in the BAYS shape from birth (same deterministic ids the
      // read-time migration mints) — `appliances[]` is a read-only legacy
      // shape for rooms persisted before 2026-08-30, never written anew.
      // Floor the layout keeps clear rides along as hard `clearance` bays,
      // clipped to the run's extent (`cw.clearances`, see the schema).
      bays: [
        ...appliances.map(applianceBayFromLegacy),
        ...clearanceBays(cw, extent, options.objects, axes),
      ],
    });
  }

  const joined = joinAutoRunsAtCorners(
    runs,
    resolve,
    options.objects,
    runlessWallIds,
    cabinetLayout.corners || null
  );
  // Each shallow storage stretch is a run of its own, beside its wall's deep
  // run (`shallowRuns.js`). Never joined at a corner — a 12" run turning into
  // a 24" one is no corner either depth has a plan for — and holding no
  // corner body.
  const shallow = options.objects?.length
    ? shallowStorageRuns(options.objects, resolve)
    : [];
  // Hold open every corner body the runs do NOT own — a single-sided unit, a
  // kite, the L of a join the layout rejected. Without this the fill packs
  // straight through the blind box.
  const cornerBays = cornerReservationsForRuns(
    joined,
    options.objects,
    resolve
  );
  // Runs are DERIVED on the resolver's kitchen-clipped walls — that is the
  // frame the generator measured in — and STORED on the raw room walls: the
  // clip is a generator concern (spec §8.4), and a run never references it.
  // `rebaseRunFrames` moves every param (segments, bays, overrides) by the
  // clip offset and stamps the raw frame, so nothing downstream needs the
  // clipped resolver again.
  const rawById = new Map(
    dedupeRoomWalls(room?.walls || []).map((w) => [w.id, w])
  );
  const clippedWall = (seg) => resolve({ id: seg.wallId, kind: seg.kind });
  const rawWall = (seg) =>
    seg.kind === 'real'
      ? rawById.get(seg.wallId) || clippedWall(seg)
      : clippedWall(seg);
  return {
    runs: [...joined, ...shallow]
      .map((r) => {
        const extra = cornerBays.get(r.id);
        return extra ? { ...r, bays: [...(r.bays || []), ...extra] } : r;
      })
      .map((r) => {
        const { run, actions } = rebaseRunFrames(r, clippedWall, rawWall);
        if (
          actions.some((a) => a.action === 'orphan' || a.action === 'missing')
        ) {
          warnings.push(
            `run ${r.id}: could not express its params on the raw room wall`
          );
          return stampRunFrames(r, clippedWall);
        }
        return run;
      }),
    warnings,
  };
}

/**
 * Two auto runs whose legs meet at a corner are one run: a counter that
 * turns is one run, whatever the generator put in the box (single-run plan,
 * 2026-09-12). The join used to be gated on the layout having built an L
 * there (#1494) so the run's own corner plan could not invent a unit on the
 * first re-solve; the generator's decision now rides on the run instead, as a
 * corner treatment override (`cornerTreatment.js`) `fillRun` honours. A
 * caller without `objects` still gets per-wall runs — with nothing to derive
 * the treatment from, joining would hand the corner to the plan after all.
 *
 * Real walls AND peninsula arms join (stage 2). A U whose back is a peninsula
 * is one counter — wall, back, arm — and the vertex where the back meets the
 * wall, or the far arm meets the back, is the arm's ROOT on the other's line
 * (`armRootVertex`), not a room vertex. Islands stay on their own builder.
 */
const {
  withCornerTreatments,
  applianceSeparatesLegs,
  isLowerBody,
} = require('../../cabinet-runs/cornerTreatment');
const { cornerUnitWallIds } = require('../../cabinet-runs/cornerUnit');

const JOINABLE_KINDS = new Set(['real', 'peninsula']);

/**
 * A run that turns from a real wall onto a peninsula keeps its uppers on the
 * wall and hangs none over the arm — `fillRun` decides that per SEGMENT from
 * the resolved wall's `isVirtual`. The per-run `uppers:false` a lone peninsula
 * run carries would switch the wall's uppers off too, so a mixed run drops it.
 */
function uppersForJoinedRun(run) {
  const kinds = new Set(run.segments.map((s) => s.kind));
  if (kinds.size === 1) return run;
  const { uppers, ...rest } = run;
  return rest;
}

function joinAutoRunsAtCorners(
  runs,
  resolve,
  objects,
  runlessWallIds,
  recordedCorners = null
) {
  const joinable = (r) =>
    JOINABLE_KINDS.has(r.segments[0]?.kind) &&
    !runlessWallIds?.has(r.segments[0]?.wallId);
  const real = runs.filter(joinable);
  if (real.length < 2 || !objects) return runs;
  const resolved = real.map((r) => ({
    ...r,
    segments: r.segments.map((s) => ({
      ...s,
      wall: resolve({ id: s.wallId, kind: s.kind }),
    })),
  }));
  // An appliance standing at the vertex ENDS the run: what lies in front of
  // it is its clearance, and the adjoining wall's cabinets are their own run
  // starting past that gap (user ruling, 2026-09-13).
  const { runs: joined, joins } = joinRunsAtCorners(resolved, {
    refuse: (segA, segB) => applianceSeparatesLegs(segA, segB, objects),
  });
  if (!joins.length) return runs;
  // Every interior vertex records what the generator built there, read off
  // the objects while the segments still carry their walls.
  const accepted = joined.map((r) => {
    const { joinedRunIds, ...rest } = r;
    return joinedRunIds
      ? withCornerTreatments(rest, objects, recordedCorners)
      : rest;
  });
  const strip = (r) =>
    uppersForJoinedRun({
      ...r,
      segments: r.segments.map(({ wall, ...s }) => s),
    });
  return [...accepted.map(strip), ...runs.filter((r) => !joinable(r))];
}

// Object types a single-wall run OWNS (and re-solves on edit). Appliances,
// corner units (2 refs), and peninsula back panels are NOT run-owned — they
// stay materializeCandidate-owned. Countertops are owned, but indirectly: they
// carry no `cabinetWallRefs` of their own, so they follow the cabinets beneath
// them (`countertopRunId`) rather than a wall.
const OWNABLE_BAY_TYPES = new Set([
  'lower-cabinet',
  'upper-cabinet',
  'corner-cabinet', // owned only when one run spans both its legs
  // The oblique corner's kite and the slab over it: the engine builds both at
  // a joined oblique vertex (`corners.js`), so like the L they are the run's
  // when one run spans both legs. Unowned, the first re-solve — and the
  // generator's own engine-executed fill — stacked a second kite on the first
  // (1,090 kites for 545 corners over the corpus, 2026-09-13).
  'corner-fill-cabinet',
  'corner-fill-countertop',
  // A `blank-panel` ONLY when it is a gap filler or an appliance end piece
  // (`category.filler`, stamped by `buildBlankPanel`). Those are the pieces
  // `fillRun` re-emits, so an unowned one meant the first re-solve stacked its
  // own filler beside the generator's instead of replacing it. A panel without
  // the mark — the 1"-deep corner cover, a peninsula's back panel — is not a
  // gap in the line and stays materializeCandidate's, per the note above.
  'blank-panel',
]);

/** Whether a candidate object of an ownable TYPE is really the run's. */
// Corner pieces are locatable only by BOTH legs (`cornerIndexForUnit`).
const TWO_LEG_TYPES = new Set([
  'corner-cabinet',
  'corner-fill-cabinet',
  'corner-fill-countertop',
]);

function isOwnableBody(obj) {
  // Processing's pocket at a run end, as deep as the room left — no run
  // builds a body shallower than its own (`runEndPocket.js`).
  if (isRunEndPocket(obj)) return false;
  if (obj?.type !== 'blank-panel') return true;
  // A peninsula arm's back panel: the engine builds it with the arm
  // (`peninsulaBack.js`), so the arm's run owns it like any filler.
  return !!obj?.category?.filler || isPeninsulaBackPanel(obj);
}

// Cabinet BODIES — the objects a countertop can sit on that a run either owns
// or deliberately doesn't. A parent outside this set (an appliance) says nothing
// about which run a slab belongs to; a parent inside it says everything.
const CABINET_BODY_TYPES = new Set([
  'lower-cabinet',
  'upper-cabinet',
  'corner-cabinet',
]);

/**
 * The extent of the cabinetry the generator actually EMITTED on a wall.
 *
 * A derived run used to span `[0, wallLen]` regardless — but the generator
 * subtracts corridor rectangles, the island reserve, wall-body blockers, zone
 * projections and bridge buffers before it fills, so the run's model claimed
 * wall the layout had deliberately left empty. The first re-solve filled it.
 *
 * Mirrors the scan path (`scanRuns.js`): the span of the LOWER members only.
 * Countertops and uppers are no members, for the reason stated there — the upper
 * zone is derived FROM the lower fill zone, and an upper reaching past the base
 * run is ordinary (over a peninsula, a dishwasher, an appliance garage), so
 * counting one makes the packer fill base cabinetry under wall the layout found
 * empty.
 *
 * Returns null when the caller passed no objects: a caller that cannot see the
 * layout's output must keep the full-wall default rather than guess a narrower
 * one.
 */
// What a run is MADE of along its wall: the bodies it builds or reserves, and
// the appliances it holds a bay for (`toRunApplianceType`). Counting every
// object but a countertop or an upper took in what a scan leaves parented to a
// wall that no run holds — a chair, a washer-dryer, a storage box the pipeline
// never made a cabinet, the slab of a corner fill — and the first edit of
// option #1 packed base cabinets over them (FB5B0707: 73" past the last
// cabinet, 2026-10-23).
const RUN_BODY_TYPES = new Set([
  'lower-cabinet',
  'corner-cabinet',
  'corner-fill-cabinet',
  'blank-panel',
  'fridge-surround',
  'oven-surround',
]);
// An appliance counts only when the layout lists it on THIS wall: option #1
// gives a range set across a corner no placement (it stands on neither wall),
// and counting it by type stretched the run over it, so the first edit packed
// base cabinets through the range (66616D52: 308 sq in, 2026-10-23).
const isRunExtentMember = (o, anchoredIds) =>
  RUN_BODY_TYPES.has(o.type) ||
  (toRunApplianceType(o.type) != null && anchoredIds.has(o.id));

/**
 * The extent of the plan's built spans on a wall, clipped to the wall the run
 * resolves on (a fridge surround's pad reaches past the wall's start; an arm
 * the walkway trimmed is shorter than the plan that ran on it), or null when
 * it built nothing.
 */
function plannedExtent(built, axes) {
  let lo = Infinity;
  let hi = -Infinity;
  for (const r of built || []) {
    if (!r || !(r.end > r.start)) continue;
    lo = Math.min(lo, r.start);
    hi = Math.max(hi, r.end);
  }
  if (!(hi > lo)) return null;
  return {
    startParam: Math.max(0, lo),
    endParam: Math.min(axes.wallLen, hi),
  };
}

/**
 * How far a run measured from OBJECTS may reach, in the clipped wall's own
 * params: the whole RAW room wall. The clip is the frame the generator
 * measures in, not the edge of the kitchen — and a generated layout never
 * builds past it (its walls answer from the plan, `plannedExtent`). Option #1
 * keeps a scanned kitchen, whose cabinetry can stand past the kitchen area's
 * edge: stopped at the clip, its run left the fridge outside it, and the first
 * edit pulled the fridge 33-54" in (New Montrose / Scan 30, 2026-10-22).
 */
function runBoundsOnWall(axes, rawWall) {
  const raw = rawWall && getWallAxes(rawWall);
  if (!raw) return { lo: 0, hi: axes.wallLen };
  const paramOf = (p) =>
    (p.x - axes.start.x) * axes.wallDir.x +
    (p.z - axes.start.z) * axes.wallDir.z;
  const a = paramOf(raw.start);
  const b = paramOf(raw.end);
  return {
    lo: Math.min(0, Math.min(a, b)),
    hi: Math.max(axes.wallLen, Math.max(a, b)),
  };
}

function emittedExtentOnWall(wallId, objects, axes, bounds, anchoredIds) {
  if (!objects?.length) return null;
  let lo = Infinity;
  let hi = -Infinity;
  for (const o of objects) {
    if (!o || !isRunExtentMember(o, anchoredIds)) continue;
    if (o.dimensions?.isUpper === true) continue;
    if (isShallowRunPiece(o) || isRunEndPocket(o)) continue;
    const refs = o.cabinetWallRefs || [];
    if (!refs.some((r) => r?.id === wallId)) continue;
    const span = spanOnWall(o, axes);
    if (!span) continue;
    lo = Math.min(lo, span.lo);
    hi = Math.max(hi, span.hi);
  }
  if (!(hi > lo)) return null;
  const { lo: min, hi: max } = bounds || { lo: 0, hi: axes.wallLen };
  return {
    startParam: Math.max(min, lo),
    endParam: Math.min(max, hi),
  };
}

/**
 * Hard `clearance` bays for the floor a cabinet wall keeps clear, clipped to
 * the run's extent (a carve-out beyond the last cabinet is already outside
 * the run). Params are the wall's own; `rebaseRunFrames` moves them with the
 * rest.
 */
function clearanceBays(cw, extent, objects, axes) {
  const lo = Math.min(extent.startParam, extent.endParam);
  const hi = Math.max(extent.startParam, extent.endParam);
  // What the generator BUILT on this wall is the truth: a later pass (the
  // anchored re-plan, the notch patch) builds where the straight fill's
  // blockers said not to, so a clearance is only the part of the carve-out
  // no BODY stands in. A blank panel is not one: the peninsula's back panel
  // runs along the arm's root through the clearance an appliance at the
  // vertex reserves, and counting it as built cut that clearance to the
  // panel's edge — the packer then stood a 24" base in front of the
  // dishwasher (2026-09-13, the first single-segment run the engine executed
  // at generate time).
  // A wall that persists the plan's body spans answers from them (`bodies`;
  // a cover — a gap or end panel, an arm's back panel — is in `built` for
  // the extent but leaves its clearance a clearance); a layout from before
  // that reads its objects.
  const built = Array.isArray(cw.built)
    ? (cw.bodies || cw.built).map((r) => ({ lo: r.start, hi: r.end }))
    : (objects || [])
        .filter(
          (o) =>
            o &&
            isLowerBody(o) &&
            !isShallowRunPiece(o) &&
            (o.cabinetWallRefs || []).some((r) => r?.id === cw.id)
        )
        .map((o) => spanOnWall(o, axes))
        .filter(Boolean);
  const out = [];
  let k = 0;
  for (const c of cw.clearances || []) {
    let pieces = [{ lo: Math.max(lo, c.start), hi: Math.min(hi, c.end) }];
    for (const b of built) {
      const next = [];
      for (const p of pieces) {
        if (b.hi <= p.lo || b.lo >= p.hi) {
          next.push(p);
          continue;
        }
        if (b.lo > p.lo) next.push({ lo: p.lo, hi: b.lo });
        if (b.hi < p.hi) next.push({ lo: b.hi, hi: p.hi });
      }
      pieces = next;
    }
    for (const p of pieces) {
      if (!(p.hi - p.lo > 1e-4)) continue;
      out.push({
        id: `bay_clearance_${cw.id}_${k}`,
        kind: 'clearance',
        segmentIndex: 0,
        anchorParam: (p.lo + p.hi) / 2,
        width: p.hi - p.lo,
        hard: true,
      });
      k += 1;
    }
  }
  return out;
}

/**
 * Hard `corner` bays reserving every corner BODY that stands on a run's wall
 * but is not the run's to rebuild.
 *
 * A corner unit the run does not own is still occupying its wall. Without a
 * reservation the fill packs cabinetry straight through it — the blind box gets
 * a door cabinet, and the single-sided unit and the new base overlap. `corner`
 * is a hard bay kind `fillSegmentZone` already understands, and unlike an
 * appliance bay nothing of its own is ever moved to it (`fillRun` keeps corner
 * bays out of the solved appliance table).
 *
 * The span is the unit's own footprint projected onto the wall, so an oblique
 * corner or a kite reserves what it really covers rather than a nominal width.
 *
 * @param {Object[]} runs   runs whose segments carry `wallId` + a resolved wall
 * @param {Object[]} objects the layout's materialized objects
 * @returns {Map<string, Object[]>} runId → bays to append
 */
function cornerReservationsForRuns(runs, objects, resolve) {
  const out = new Map();
  if (!objects?.length) return out;
  // Which run (if any) OWNS each corner unit — the same rule `assignRunIds`
  // applies, evaluated here because ownership is stamped later. An owned corner
  // is rebuilt by the fill, so reserving it too would double-book the span.
  const runIdByWallId = new Map();
  for (const run of runs || []) {
    for (const seg of run.segments || []) {
      if (seg.wallId) runIdByWallId.set(seg.wallId, run.id);
    }
  }
  const ownerOf = (unit) => {
    const ids = cornerUnitWallIds(unit);
    if (ids.length < 2) return null;
    const runIds = new Set(ids.map((id) => runIdByWallId.get(id)));
    if (runIds.size !== 1 || runIds.has(undefined)) return null;
    return [...runIds][0];
  };
  const corners = objects.filter(
    (o) =>
      o?.type === 'corner-cabinet' &&
      o.dimensions?.isUpper !== true &&
      o.center &&
      o.dimensions
  );
  if (!corners.length) return out;

  for (const run of runs || []) {
    const bays = [];
    (run.segments || []).forEach((seg, segmentIndex) => {
      const wall = resolve({ id: seg.wallId, kind: seg.kind });
      const axes = wall && getWallAxes(wall);
      if (!axes) return;
      for (const unit of corners) {
        const ids = cornerUnitWallIds(unit);
        if (!ids.includes(seg.wallId)) continue;
        // Owned corners are rebuilt by the fill; only the ones it cannot
        // reproduce need holding open.
        if (ownerOf(unit) === run.id) continue;
        const span = spanOnWall(unit, axes);
        if (!span) continue;
        const lo = Math.max(span.lo, Math.min(seg.startParam, seg.endParam));
        const hi = Math.min(span.hi, Math.max(seg.startParam, seg.endParam));
        if (!(hi - lo > 1e-4)) continue;
        bays.push({
          id: `bay_corner_${unit.id}_${segmentIndex}`,
          kind: 'corner',
          segmentIndex,
          anchorParam: (lo + hi) / 2,
          width: hi - lo,
          hard: true,
        });
      }
    });
    if (bays.length) out.set(run.id, bays);
  }
  return out;
}

/** An object's extent along a wall axis, from its rotated footprint. */
function spanOnWall(obj, axes) {
  const c = obj?.center;
  const w = obj?.dimensions?.x;
  const d = obj?.dimensions?.z;
  if (!c || !(w > 0) || !(d > 0)) return null;
  // `footprintFor`, not `calculateCorners(..., rotation.y)`: a stored rotation
  // is a three.js Y-Euler, which that helper mirrors on any non-axis wall.
  const pts = footprintFor({ x: c.x, z: c.z }, w, d, obj.rotation?.y || 0);
  const params = pts.map(
    (p) =>
      (p.x - axes.start.x) * axes.wallDir.x +
      (p.z - axes.start.z) * axes.wallDir.z
  );
  return { lo: Math.min(...params), hi: Math.max(...params) };
}

/**
 * The objects a countertop says it rests on. Its `parents`, or — for a strip
 * filler's slab saved before it named one — the filler it was built with.
 */
function countertopParents(countertop) {
  if (countertop?.parents?.length) return countertop.parents;
  const stripFiller = stripFillerCabinetIdOf(countertop);
  return stripFiller ? [{ id: stripFiller, type: 'lower-cabinet' }] : [];
}

/**
 * The run a countertop belongs to, or null if no run owns it.
 *
 * A countertop carries no wall ref — `buildCountertopRuns` gives it `parents`
 * listing every counter-height item it spans — so it follows the cabinets
 * BENEATH it. Ownership requires unanimity among the cabinet bodies it covers:
 *
 *   - appliance / unresolvable parents are ignored (a slab spanning a
 *     dishwasher is still an ordinary run slab);
 *   - a cabinet body owned by a DIFFERENT run, or by no run at all (an auto
 *     corner unit, which v1 leaves materializeCandidate-owned), disqualifies
 *     the slab entirely.
 *
 * The second rule is what keeps an L-corner slab and a peninsula seating
 * overhang unowned: a run re-solve would delete them and emit only its own
 * leg's slab, silently taking the counter off the corner.
 *
 * @param {Object} countertop
 * @param {Map<string, Object>} objectsById  every object in the room, by id,
 *   already carrying whatever `runId` it has.
 * @returns {string|null}
 */
function countertopRunId(countertop, objectsById) {
  let found = null;
  for (const p of countertopParents(countertop)) {
    const parent = objectsById.get(p?.id);
    if (!parent) continue; // gone from the room — nobody can re-emit it
    if (!CABINET_BODY_TYPES.has(parent.type)) continue; // appliance, etc.
    if (!parent.runId) return null; // an unowned body → shared slab
    if (found && found !== parent.runId) return null; // spans two runs
    found = parent.runId;
  }
  return found;
}

/**
 * Stamp `runId` onto the objects a derived run owns, so the manual edit UI
 * (which gates on `selectedObject.runId`) lights up on auto kitchens.
 *
 * v1 ownership: a lower/upper cabinet bay (or filler — still `type:
 * 'lower-cabinet'`) whose SINGLE `cabinetWallRef` points to a wall that got a
 * run. Corner units carry two `parentWallIds`/refs (buildCornerCabinet.js:111)
 * so `refs.length !== 1` excludes them; appliance objects aren't ownable types.
 * Never clobbers an existing `runId` (e.g. a manually-drawn run).
 *
 * Pure — returns a new array; objects that gain a runId are shallow-cloned.
 *
 * @param {Object[]} objects
 * @param {Object[]} runs  output of `cabinetLayoutToRuns().runs`
 * @returns {Object[]}
 */
/** The run on the one cabinet wall an object names, or null. */
function runOfSingleRef(obj, runsByWallId) {
  const refs = obj.cabinetWallRefs;
  if (!Array.isArray(refs) || refs.length !== 1) return null;
  return (
    runForPieceOnWall(obj, refs[0]?.id, runsByWallId.get(refs[0]?.id)) || null
  );
}

function assignRunIds(objects, runs) {
  // A wall can carry two runs — its own and a shallow storage run beside it
  // (`shallowRuns.js`) — so a wall names the runs on it, and a piece is told
  // to the right one (`runForPieceOnWall`).
  const runsByWallId = new Map();
  for (const run of runs || []) {
    for (const seg of run.segments || []) {
      if (!seg.wallId) continue;
      if (!runsByWallId.has(seg.wallId)) runsByWallId.set(seg.wallId, []);
      const list = runsByWallId.get(seg.wallId);
      if (!list.includes(run)) list.push(run);
    }
  }
  // Pass 1 — the bays, by their single cabinet-wall ref.
  const tagged = (objects || []).map((obj) => {
    if (obj.runId) return obj;
    if (!OWNABLE_BAY_TYPES.has(obj.type) || !isOwnableBody(obj)) return obj;
    const refs = obj.cabinetWallRefs;
    if (!Array.isArray(refs) || !refs.length) return obj;
    // A corner unit is the run's ONLY when it names both legs and one run
    // spans both (a corner-joined run, #1494). The test is what it looks
    // like: a unit that names one wall cannot be located at a corner —
    // `cornerIndexForUnit` returns -1 for fewer than two wall ids — so
    // owning it means the fill deletes a unit it can neither identify nor
    // give back, and packs a door cabinet into the blind box. Not ownable;
    // RESERVED instead (see `cornerReservationsForRuns`).
    //
    // This USED to catch every single-sided corner unit, because
    // `buildSingleSidedCornerCabinet` stamped one wall id where its L-shaped
    // sibling stamped two. It now stamps both — the unit's face is on one
    // wall and its end stands against the other — so a freshly built one is
    // ownable, locatable, and rebuilt by the same fill that emits it, like
    // any L corner. What still lands here is a unit from before that change,
    // or any hand-authored one naming a single wall: still unlocatable,
    // still reserved rather than owned.
    if (TWO_LEG_TYPES.has(obj.type) && refs.length < 2) return obj;
    const ids = new Set(
      refs.map((r) => runForPieceOnWall(obj, r?.id, runsByWallId.get(r?.id)))
    );
    if (ids.size !== 1 || ids.has(undefined)) return obj;
    const [runId] = ids;
    return { ...obj, runId };
  });

  // Pass 2 — the countertops follow the bays pass 1 just tagged. Must run
  // second: `countertopRunId` reads the `runId`s pass 1 writes. The strip
  // behind a peninsula's range stands over no cabinet — only the arm's back
  // panel — and is the arm's run's, by the arm it names, as the panel is.
  const byId = new Map(tagged.filter((o) => o?.id).map((o) => [o.id, o]));
  const withSlabs = tagged.map((o) => {
    if (o.runId || o.type !== 'countertop') return o;
    const runId =
      countertopRunId(o, byId) ||
      (isPeninsulaBackBridge(o) ? runOfSingleRef(o, runsByWallId) : null);
    return runId ? { ...o, runId } : o;
  });
  // Pass 3 — the appliance objects the runs' bays anchor carry their bayId
  // (ownership stays as it is: see ownership.js).
  return ownFridgeSurrounds(stampApplianceBays(withSlabs, runs), runs);
}

/**
 * A fridge surround is the run's whose bay holds its fridge.
 *
 * It is cabinetry the run builds around the appliance (`applianceInstall.js`),
 * and an auto run's first re-solve rebuilds it and stamps the run on it
 * (`policy.rebuildInstall`) — so left to the apply it belonged to the run
 * after any edit and to nobody before one: no run section when it was
 * clicked on a kitchen just applied. Owning one is safe exactly where the
 * run rebuilds it, which is the bay test. A range hood is not touched: it is
 * a product standing on the range, not a box in the run's finish.
 */
function ownFridgeSurrounds(objects, runs) {
  const claims = (runs || [])
    .map((run) => {
      const ids = applianceObjectIds(run);
      const appliances = (objects || []).filter((o) => o?.id && ids.has(o.id));
      return { runId: run.id, appliances };
    })
    .filter((c) => c.appliances.length);
  if (!claims.length) return objects;
  let changed = false;
  const out = objects.map((o) => {
    if (o?.type !== 'fridge-surround' || o.runId) return o;
    const claim = claims.find((c) => isInstallPieceOf(o, c.appliances));
    if (!claim) return o;
    changed = true;
    return { ...o, runId: claim.runId };
  });
  return changed ? out : objects;
}

module.exports = { cabinetLayoutToRuns, assignRunIds, countertopRunId };
